import { UniButton } from '@/design-system/components/buttons';
import { UniIconButton } from '@/design-system/components/buttons';
import { TextTitle } from '@/design-system/components/typography';
import { SxProp, View, useDripsyTheme } from 'dripsy';
import _, { debounce } from 'lodash';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput } from '@/design-system/components/inputs';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { NewAccountStackParamList, NewAccountStackRoutes } from './type';
import { PageHeaderModalNative } from '../page/PageHeaderModalNative';
import { Dropdown } from '@/design-system/components/dropdown';
import { CurrenciesContext } from '@/currencies/currency.context';

export interface NewAccountProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  onData: (data: any) => void;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
}

export const NewAccountNativeZeroScreen: React.FC<NewAccountProps> = (props) => {
  const { onClose, onLayout, onData, func, onReturnData, refresh } = props;
  const { t } = useTranslation('account-management');
  const { body, textMb, btnView, btn } = sxStyles;

  const { currencies } = useContext(CurrenciesContext);
  const [filter, setFilter] = useState<string>('');
  const accountAssetsFilter = useMemo(() => {
    return currencies
      ?.map((curr) => {
        return {
          label: curr.display_symbol,
          value: curr.display_symbol
        };
      })
      .filter((item) => {
        if (filter && filter.length > 0) {
          return item.value.toLowerCase().includes(filter.toLowerCase());
        }
        return true;
      });
  }, [currencies, filter]);

  const navigation = useNavigation<NativeStackNavigationProp<NewAccountStackParamList>>();

  const [account, setAccount] = useState<any>({
    name: undefined,
    assets: undefined
  });
  const [errorMsg, setErrorMsg] = useState<any>({ name: undefined, assets: undefined });

  const changeNewAccount = () => {
    let validate = true;
    let errorMsgFlag = { assets: undefined, name: undefined };
    if (!account.name) {
      setErrorMsg({ ...errorMsgFlag, name: t('card-account-management::credit-card-new-account.glossary.field-required') });
      validate = false;
    } else if (!account.assets) {
      setErrorMsg({ ...errorMsgFlag, assets: t('card-account-management::credit-card-new-account.glossary.field-required') });
      validate = false;
    } else setErrorMsg(errorMsgFlag);

    if (validate) {
      onData({ data: account });
      navigation.navigate(NewAccountStackRoutes.Confirm, { onLayout, data: account, func, onReturnData, refresh });
    }
  };

  const { theme } = useDripsyTheme();

  const searchValue = useCallback(
    debounce((value) => setFilter(value), 300),
    []
  );

  return (
    <View sx={{ height: '100%', backgroundColor: '$white' }}>
      <PageHeaderModalNative>
        <View sx={{ px: '$4', mt: '$5', mb: '$4' }}>
          <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
            <UniIconButton
              icon="arrowLeft"
              options={{ iconStyles: { color: theme?.colors?.$white as string } }}
              onPress={onClose}></UniIconButton>
          </View>
          <View sx={{ textAlign: 'center' }}>
            <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
              {t('card-account-management::credit-card-new-account.glossary.new-account')}
            </TextTitle>
          </View>
        </View>
      </PageHeaderModalNative>
      <View sx={body}>
        <TextTitle sx={textMb} semiBold>
          {t('card-account-management::credit-card-new-account.new-account-page.text--fill-out-to-create-account')}
        </TextTitle>
        <View sx={{ gap: '$5' }}>
          <UniInput
            value={account.name}
            labelText={t('card-account-management::credit-card-new-account.glossary.account-name')}
            errorText={errorMsg.name}
            onValueChange={(value) => {
              setAccount({
                ...account,
                name: value?.toUpperCase()
              });
            }}
          />
          <View>
            <Dropdown
              value={account.assets}
              label={t('card-account-management::credit-card-new-account.glossary.assets')}
              multiple
              options={accountAssetsFilter || []}
              sx={{ width: '100%' }}
              onSearch={searchValue}
              onValueChange={(value) => {
                setAccount({
                  ...account,
                  assets: value
                });
              }}
            />
          </View>
        </View>
        <View sx={btnView}>
          <UniButton
            sx={btn}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::credit-card-new-account.glossary.button--update')}
            onPress={changeNewAccount}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    px: 16,
    py: 20
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    width: '92%',
    position: 'fixed',
    left: 0,
    bottom: 0,
    marginX: 15,
    marginBottom: 30,
    zIndex: -1
  },
  btn: {
    width: '100%',
    height: 44
  }
};
